import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Box, IconButton, InputAdornment, Paper, TextField, Typography, Autocomplete } from "@mui/material";
import { useFormik } from "formik";
import * as yup from "yup";
import { useSelector } from "react-redux";
import { Visibility, VisibilityOff, CheckCircle, Cancel } from "@mui/icons-material";
import { signOut } from "aws-amplify/auth";
import { UUID } from "crypto";
import {
  Checkbox,
  CountrySelect,
  FieldWrapper,
  LinkTerminosPrivacidad,
  StepActions,
  PageHeader,
} from "@components/ui_v2";
import { RootState, useAppDispatch } from "@store/store";
import {
  setAccountUser,
  setConditionLoan,
  setTaxProductId,
  setLoanStatus,
  setTaxSystemType,
  setGuaranteeTypeId,
  setGuaranteeTypes,
  setFinancedServicesId,
  setIsPreviousClient,
  setRfcApprovedCompany,
  setMotorbikeInformation,
  setConvenioId,
  setQuoterDetails,
  setLeasingTypeId,
  setEquipmentDescription,
  setTaxSystemCodes,
  setSelectedProductTypeIds,
  setRequestedInterestRate,
  setQuoteContact,
  setAccountType,
} from "@store/slices/registerSlice";
import { setModalErrorVisible } from "@store/slices/appSlice";
import { useSaveLeadDataMutation } from "@api/onboarding";
import { PayloadSaveLeadData } from "@interfaces/request";
import { calculateAmountMonthly, getConfigName } from "@helpers/funciones";
import { getCrearCuentaConfig } from "@helpers/crearCuentaConfig";
import { ApiError } from "@interfaces/response";
import { AccountType, QuoteContact, QuoterDetails, TaxSystemCodes } from "@interfaces/store";
import {
  EMAIL,
  PASSWORD,
  PASSWORD_ALLOWED_CHARS,
  HAS_SPECIAL_CHARACTERS,
  HAS_NUMBERS,
  HAS_UPPERCASE,
  HAS_LOWERCASE,
  HAS_WHITESPACE,
  PHONE_NUMBER,
} from "@utils/expreciones";
import { PATHS_AUTH_V2, PATHS_COTIZADORES_V2, PATHS_TOFU_V2, STATUS_CATALOG } from "@helpers/constantes";

const domainOptions = ["gmail.com", "outlook.com", "hotmail.com"];

/** Marca específicamente el caso "faltan/son inválidos los query params requeridos" (→ navigate a /login),
 * distinto de cualquier otro error inesperado de parseo (→ solo se loguea, sin navegar). */
class InvalidAccountQueryParamsError extends Error {}

/** Usuario de la cuenta según la liga (`account_type=phone|email`): sin la bandera, correo. */
const getAccountType = (search: string): AccountType =>
  new URLSearchParams(search).get("account_type") === "phone" ? "phone" : "email";

interface AccountQueryParams {
  taxSystemId: UUID;
  productId: UUID;
  taxSystemType: "PM" | "PFAE" | "PF";
  loanAmount: number;
  loanTerm: number;
  amountMonthly: number;
  sourceCode: string;
  financedServicesId: string | null;
  guaranteeTypeId: string | null;
  descriptionGuarantee: string;
  taxSystemCode: string | null;
  rfcCompany: string | null;
  advancePercentage: number | null;
  residualPercentage: number | null;
  guaranteeTypes: string[];
  motorbikeCatalogId: string | undefined;
  convenioId: string | null;
  /** Tipo de arrendamiento elegido en el cotizador (Vivemoto, Mercader). */
  leasingTypeId: string | null;
  /** Descripción del equipo que captura el cotizador de Mercader. */
  equipmentDescription: string | null;
  /** Tax system por régimen (Flux, `{"PM":"FLXPM","PFAE":"FLXPF"}`): el registro elige el del RFC. */
  taxSystemCodes: TaxSystemCodes | null;
  quoterDetails: QuoterDetails | null;
  isPreviousClient: boolean | null;
  /** Tasa anual (%) que eligió el usuario en el cotizador (Flux). */
  interestRate: number | null;
  /** Nombre comercial y teléfono del cotizador (Flux) para prellenar el registro. */
  quoteContact: QuoteContact | null;
  /** Ids del catálogo `product_type` elegidos en el cotizador de la landing (Pymio). */
  selectedProductTypeIds: string[] | null;
  /** Correo o celular como usuario de la cuenta (WuWei: celular). */
  accountType: AccountType;
}

/**
 * Extrae y valida los datos que la pantalla anterior (selección de producto) pasa por query string.
 * Lanza `InvalidAccountQueryParamsError` si faltan los campos requeridos o el tax_system_type no es válido;
 * cualquier otro error (ej. JSON malformado en quoter_details) se propaga como una excepción normal.
 */
const parseAccountQueryParams = (search: string): AccountQueryParams => {
  const searchParams = new URLSearchParams(search);

  const taxSystemId = searchParams.get("tax_system_id") as UUID;
  const productId = searchParams.get("product_id") as UUID;
  const taxSystemType = searchParams.get("tax_system_type");
  const loanAmount = Number(searchParams.get("loan_amount"));
  const loanTerm = Number(searchParams.get("loan_term"));
  const advancePercentage = Number(searchParams.get("advance_percentage"));
  const residualPercentage = Number(searchParams.get("residual_percentage"));
  const guaranteeTypes = (searchParams.get("guarantee_types") ?? "")
    .split(",")
    .map((value) => value.trim())
    .filter((value) => value.length > 0);

  const quoterDetailsParam = searchParams.get("quoter_details");
  const quoterDetails: QuoterDetails | null = quoterDetailsParam ? JSON.parse(quoterDetailsParam) : null;

  const taxSystemCodesParam = searchParams.get("tax_system_codes");
  const taxSystemCodes: TaxSystemCodes | null = taxSystemCodesParam ? JSON.parse(taxSystemCodesParam) : null;

  const selectedProductTypeIdsParam = searchParams.get("selected_product_type_ids");
  const selectedProductTypeIds: string[] = selectedProductTypeIdsParam ? JSON.parse(selectedProductTypeIdsParam) : [];

  const isPreviousClientParam = searchParams.get("is_previous_client");
  const isPreviousClient = isPreviousClientParam === "true" ? true : isPreviousClientParam === "false" ? false : null;

  const interestRate = Number(searchParams.get("interest_rate"));
  const tradeName = searchParams.get("trade_name")?.trim() ?? "";
  const phoneNumber = searchParams.get("phone_number") ?? "";
  const quoteContact: QuoteContact | null =
    tradeName || phoneNumber
      ? { trade_name: tradeName, code_phone: searchParams.get("code_phone_number") || "+52", phone: phoneNumber }
      : null;

  if (!taxSystemId || !productId || !taxSystemType || !loanAmount || !loanTerm) {
    throw new InvalidAccountQueryParamsError();
  }
  if (taxSystemType !== "PM" && taxSystemType !== "PFAE" && taxSystemType !== "PF") {
    throw new InvalidAccountQueryParamsError();
  }

  return {
    taxSystemId,
    productId,
    taxSystemType,
    loanAmount,
    loanTerm,
    amountMonthly: calculateAmountMonthly(loanAmount, loanTerm),
    sourceCode: String(searchParams.get("source_code") || ""),
    financedServicesId: searchParams.get("financed_services_id") ?? null,
    guaranteeTypeId: searchParams.get("guarantee_type_id") ?? null,
    descriptionGuarantee: String(searchParams.get("description_guarantee") || ""),
    taxSystemCode: searchParams.get("person_type"),
    rfcCompany: searchParams.get("rfcCompany"),
    advancePercentage: Number.isNaN(advancePercentage) ? null : advancePercentage,
    residualPercentage: Number.isNaN(residualPercentage) ? null : residualPercentage,
    guaranteeTypes,
    motorbikeCatalogId: searchParams.get("motorbike_catalog_id") ?? undefined,
    convenioId: searchParams.get("convenio_id") ?? null,
    leasingTypeId: searchParams.get("leasing_type_id") ?? null,
    equipmentDescription: searchParams.get("equipment_description") || null,
    taxSystemCodes,
    quoterDetails,
    isPreviousClient,
    interestRate: interestRate > 0 ? interestRate : null,
    quoteContact,
    selectedProductTypeIds: selectedProductTypeIds.length ? selectedProductTypeIds : null,
    accountType: getAccountType(search),
  };
};

const RuleRow = ({ ok, label }: { ok: boolean; label: string }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
    {ok ? (
      <CheckCircle sx={{ fontSize: 15, color: "success.main" }} />
    ) : (
      <Cancel sx={{ fontSize: 15, color: "error.main" }} />
    )}
    <Typography variant="body2" sx={{ color: ok ? "success.main" : "error.main" }}>
      {label}
    </Typography>
  </Box>
);

export const CrearCuenta = () => {
  const navigate = useNavigate();
  const { terminosLabel, promociones } = getCrearCuentaConfig(getConfigName(window.location.href));
  const location = useLocation();
  // De la liga y no del store: el store se limpia y se llena en el efecto, después del primer render.
  const isPhoneAccount = getAccountType(location.search) === "phone";
  const dispatch = useAppDispatch();

  const application = useSelector((state: RootState) => state.register.application);
  const accountUser = useSelector((state: RootState) => state.register.account_user);

  /**
   * Regresa al cotizador del tenant (`CotizadoresV2`, que cae al legacy si el tenant no está
   * migrado) en vez del cotizador genérico del wizard. Se reenvía el `source_code` como `code`,
   * porque el cotizador lo borra de sessionStorage cuando no viene en la URL.
   */
  const handleBack = () => {
    const sourceCode = new URLSearchParams(location.search).get("source_code");
    const query = sourceCode ? `?code=${encodeURIComponent(sourceCode)}` : "";
    navigate(`${PATHS_COTIZADORES_V2.credito_simple.path}${query}`);
  };

  const [saveLeadData] = useSaveLeadDataMutation();

  const [showPassword, setShowPassword] = useState(false);

  const formik = useFormik({
    initialValues: {
      email: accountUser.email || "",
      country_code: accountUser.code_phone || "+52",
      phone: accountUser.phone || "",
      password: accountUser.password || "",
      terms: false,
      promotions: true,
    },
    // Sin esto, al entrar con los campos vacíos `isValid` es `true` y Continuar se ve habilitado (QA CTA-01).
    validateOnMount: true,
    validationSchema: yup.object().shape({
      // Check obligatorio de términos (`S_Usuario consent="terminos"` del prototipo): Continuar queda deshabilitado
      // (`isValid`) hasta aceptarlo, sin mensaje de error. Sin casilla (Mercader), no se pide.
      terms: terminosLabel === null ? yup.boolean() : yup.boolean().oneOf([true]),
      email: isPhoneAccount
        ? yup.string()
        : yup
            .string()
            .required("Campo requerido")
            .matches(EMAIL, "Asegúrate de incluir el @ y los puntos necesarios (.com .mx)"),
      phone: isPhoneAccount
        ? yup.string().required("Campo requerido").matches(PHONE_NUMBER, "Número de teléfono inválido")
        : yup.string(),
      password: yup
        .string()
        .required("Campo requerido")
        .test("password_rules", function (value) {
          if (!value) return true;

          if (value.length > 50) {
            return this.createError({ message: "Excede la longitud permitida (50 caracteres)." });
          }

          if (!PASSWORD_ALLOWED_CHARS.test(value)) {
            return this.createError({
              message: "Estás usando caracteres no permitidos. Intenta con otros caracteres.",
            });
          }

          if (!PASSWORD.test(value)) {
            return this.createError({
              message:
                "Formato incorrecto. Debe contener una letra mayúscula, número, carácter especial y más de 8 caracteres.",
            });
          }

          return true;
        }),
    }),
    onSubmit: async (values, { setSubmitting }) => {
      try {
        if (!formik.isValid) {
          return;
        }

        setSubmitting(true);
        const email = isPhoneAccount ? "" : values.email;
        const payloadSaveLeadData: PayloadSaveLeadData = {
          email,
          amount: application.loan_amount,
          product_id: application.product_id,
          tax_system_id: application.tax_system_id,
          term: application.loan_term,
          ...(isPhoneAccount && {
            account_type: "phone",
            code_phone_number: values.country_code,
            phone_number: values.phone,
          }),
          ...(promociones && { accept_promotions: values.promotions }),
        };
        await saveLeadData(payloadSaveLeadData).unwrap();
        await signOut();

        const { code } = STATUS_CATALOG.registro_datos_personales;

        dispatch(setLoanStatus(code));
        dispatch(
          setAccountUser({
            id: 0,
            email,
            password: values.password,
            // `setAccountUser` separa la lada del número; `RegistroDatosPersonales` lo prellena.
            ...(isPhoneAccount && { phone: `${values.country_code}${values.phone}` }),
          }),
        );

        navigate(PATHS_TOFU_V2.registroDatosPersonales.path);
      } catch (error) {
        const api_error = error as ApiError;
        if (api_error.data) {
          if (api_error.data.code === "001") {
            if (isPhoneAccount) {
              formik.setFieldError("phone", "Este celular ya está registrado, intenta con otro o inicia sesión");
            } else {
              formik.setFieldError("email", "Este correo ya esta registrado, intenta con otro o inicia sesión");
            }
            return;
          }
        }

        dispatch(setModalErrorVisible({ open: true, error: api_error }));
      } finally {
        setSubmitting(false);
      }
    },
  });

  const passwordRules = [
    {
      label: "Entre 8 y 50 caracteres",
      ok: formik.values.password.length >= 8 && formik.values.password.length <= 50,
    },
    { label: "Un carácter numérico", ok: HAS_NUMBERS.test(formik.values.password) },
    { label: "Un carácter especial", ok: HAS_SPECIAL_CHARACTERS.test(formik.values.password) },
    { label: "Una mayúscula", ok: HAS_UPPERCASE.test(formik.values.password) },
    { label: "Una minúscula", ok: HAS_LOWERCASE.test(formik.values.password) },
    { label: "Sin espacios en blanco", ok: !HAS_WHITESPACE.test(formik.values.password) },
  ];

  const getDomainSuggestions = (value: string): string[] => {
    const parts = value.split("@");
    if (parts.length > 1) {
      const enteredDomain = parts[1].toLowerCase();
      return domainOptions
        .filter((option) => option.startsWith(enteredDomain))
        .map((domain) => `${parts[0]}@${domain}`);
    }
    return [];
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  useEffect(() => {
    try {
      const params = parseAccountQueryParams(location.search);

      localStorage.removeItem("isLoggedOut");

      dispatch({ type: "CLEAN_STORE" });
      dispatch(setTaxSystemType(params.taxSystemType));
      dispatch(
        setTaxProductId({
          tax_system_id: params.taxSystemId,
          product_id: params.productId,
          source_code: params.sourceCode,
          type: String(params.taxSystemCode),
        }),
      );
      dispatch(
        setConditionLoan({
          loanAmount: params.loanAmount,
          loanTerm: params.loanTerm,
          amountMonthly: params.amountMonthly,
          advance_percentage: params.advancePercentage,
          residual_percentage: params.residualPercentage,
        }),
      );
      dispatch(
        setGuaranteeTypeId({
          guarantee_type_id: params.guaranteeTypeId,
          description_guarantee: params.descriptionGuarantee,
        }),
      );
      dispatch(setGuaranteeTypes({ guarantee_types: params.guaranteeTypes }));
      dispatch(setFinancedServicesId({ financed_services_id: params.financedServicesId }));
      dispatch(setIsPreviousClient({ is_previous_client: params.isPreviousClient }));
      dispatch(setMotorbikeInformation({ motorbikeCatalogId: params.motorbikeCatalogId }));
      if (params.rfcCompany && params.rfcCompany.trim() !== "") {
        dispatch(setRfcApprovedCompany({ rfc_approved_company: params.rfcCompany }));
      }
      if (params.convenioId) {
        dispatch(setConvenioId({ convenio_id: params.convenioId }));
      }
      dispatch(setLeasingTypeId({ leasing_type_id: params.leasingTypeId }));
      dispatch(setEquipmentDescription({ equipment_description: params.equipmentDescription }));
      dispatch(setTaxSystemCodes({ tax_system_codes: params.taxSystemCodes }));
      dispatch(setRequestedInterestRate({ requested_interest_rate: params.interestRate }));
      dispatch(setQuoteContact({ quote_contact: params.quoteContact }));
      dispatch(setSelectedProductTypeIds({ selected_product_type_ids: params.selectedProductTypeIds }));
      dispatch(setAccountType({ account_type: params.accountType }));
      if (params.quoterDetails) {
        dispatch(setQuoterDetails(params.quoterDetails));
      }
    } catch (error) {
      console.error("Ha ocurrido un error: ", error);
      if (error instanceof InvalidAccountQueryParamsError) {
        // Sin los params de la cotización no se puede crear la cuenta: login v2 (antes, el legacy).
        navigate(PATHS_AUTH_V2.login.path);
        return;
      }
    }
    // `dispatch` y `navigate` son estables; `location.search` solo cambia si cambia la cotización de la url.
  }, [dispatch, location.search, navigate]);

  return (
    <Box>
      <PageHeader
        eyebrow="Cuenta"
        title="Crea tu cuenta"
        subtitle={
          isPhoneAccount
            ? "Tu número celular será tu usuario: ahí te enviaremos el código de verificación."
            : "Te enviaremos las novedades de tu solicitud a este correo."
        }
      />

      <Paper variant="lendia_paper" sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {isPhoneAccount ? (
          <FieldWrapper label="Teléfono celular" full error={(formik.touched.phone && formik.errors.phone) || ""}>
            <Box sx={{ display: "flex", gap: 1 }}>
              <CountrySelect
                value={formik.values.country_code}
                onChange={(value) => formik.setFieldValue("country_code", value)}
                disabled={formik.isSubmitting}
              />
              <TextField
                autoComplete="off"
                size="lendia_input"
                fullWidth
                value={formik.values.phone}
                onChange={(e) => formik.setFieldValue("phone", e.target.value.replace(/[^0-9]/g, "").slice(0, 10))}
                onBlur={formik.handleBlur}
                name="phone"
                placeholder="55 1234 5678"
                disabled={formik.isSubmitting}
                inputProps={{ inputMode: "tel", "data-testid": "crear-cuenta-telefono-input" }}
              />
            </Box>
          </FieldWrapper>
        ) : (
          <FieldWrapper label="Correo electrónico" full error={(formik.touched.email && formik.errors.email) || ""}>
            <Autocomplete
              freeSolo
              options={getDomainSuggestions(formik.values.email)}
              inputValue={formik.values.email}
              onInputChange={(_e, newInputValue) => {
                const low = newInputValue.toLowerCase().trim();
                formik.setFieldValue("email", low);
              }}
              renderInput={(params) => (
                <TextField
                  autoComplete="off"
                  {...params}
                  size="lendia_input"
                  fullWidth
                  // El input del `Autocomplete` no lleva `name`, así que `handleBlur` no sabría qué campo marcar:
                  // se marca explícito para que aparezca el mensaje de formato (QA CTA-01).
                  onBlur={() => formik.setFieldTouched("email", true)}
                  inputProps={{ ...params.inputProps, maxLength: 100, "data-testid": "crear-cuenta-email-input" }}
                  placeholder="correo@empresa.com"
                />
              )}
              disabled={formik.isSubmitting}
            />
          </FieldWrapper>
        )}
        <FieldWrapper label="Contraseña" full error={(formik.touched.password && formik.errors.password) || ""}>
          <TextField
            autoComplete="off"
            size="lendia_input"
            fullWidth
            type={showPassword ? "text" : "password"}
            value={formik.values.password}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            name="password"
            disabled={formik.isSubmitting}
            inputProps={{ "data-testid": "crear-cuenta-password-input" }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={handleClickShowPassword}
                    edge="end"
                    data-testid="crear-cuenta-password-toggle"
                  >
                    {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </FieldWrapper>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          {passwordRules.map((rule) => (
            <RuleRow key={rule.label} ok={rule.ok} label={rule.label} />
          ))}
        </Box>
        {terminosLabel !== null && (
          <Box sx={{ mt: 2 }}>
            <Checkbox
              variant="brand"
              checked={formik.values.terms}
              onChange={(checked) => formik.setFieldValue("terms", checked)}
              disabled={formik.isSubmitting}
              label={terminosLabel}
            />
          </Box>
        )}
        {promociones && (
          <Box sx={{ mt: 2 }}>
            <Checkbox
              variant="brand"
              checked={formik.values.promotions}
              onChange={(checked) => formik.setFieldValue("promotions", checked)}
              disabled={formik.isSubmitting}
              label="Quiero recibir noticias y promociones"
            />
          </Box>
        )}
      </Paper>

      <StepActions
        onBack={handleBack}
        onNext={formik.submitForm}
        backLabel="Atrás"
        nextDisabled={!formik.isValid || formik.isSubmitting}
        backTestId="crear-cuenta-back-button"
        nextTestId="crear-cuenta-continue-button"
      />
      {/* Sin casilla de términos (`consent="promos"`): se aceptan al continuar. */}
      {promociones && (
        <Typography variant="body2" sx={{ mt: 2, textAlign: "center", color: "tokens.textSecondary" }}>
          Al continuar aceptas nuestros{" "}
          <LinkTerminosPrivacidad termsLabel="términos" privacyLabel="política de privacidad" />.
        </Typography>
      )}
    </Box>
  );
};
